import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import VippstarLogo from "../../assets/vippstar_logo.png";
import PatientSidebar from "../../components/PatientSidebar";
import { API_URL } from "../../config/constants";
import {
  Layout, Main,
  TopBar, TopBarLogo, TopBarContent,
  SectionTitle, ProgressBarWrapper, ProgressBarTrack, ProgressBarFill, ProgressLabel,
  TabsRow, Tab,
  ActivitiesGrid, ActivityCard, CardHeader, CardIcon, CardName, LoggedBadge,
  TagsRow, Tag, CardDescription, CardButton,
} from "./styles";

const ActivityIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="5" r="2"/>
    <path d="M12 7v5"/>
    <path d="M9 12l3 2 3-2"/>
    <path d="M9 17l3 2 3-2"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ACTIVITY_TAGS = {
  1:  ["Fine M.S.", "Pincer grasp"],        2:  ["Fine M.S.", "In-hand manipulation"],
  3:  ["Fine M.S.", "In-hand manipulation"], 4:  ["Fine M.S.", "Finger isolation"],
  5:  ["Fine M.S.", "Finger isolation"],     6:  ["Gross M.S.", "Postural control"],
  7:  ["Gross M.S.", "Postural control"],    8:  ["Gross M.S.", "Balance"],
  9:  ["Vision", "Fixation"],                10: ["Vision", "Smooth pursuit"],
  11: ["Vision", "Smooth pursuit"],          12: ["Vision", "Auditory"],
  13: ["Vision", "Vestibular"],              14: ["Vision", "Gustatory"],
  15: ["Vision", "Vestibular"],              16: ["Vision", "Auditory"],
  17: ["Memory", "Short-term"],              18: ["Attention", "Focus"],
  19: ["Language", "Comprehension"],         20: ["Problem solving"],
  21: ["Executive function"],                22: ["Planning", "Sequencing"],
  23: ["Working memory"],                    24: ["Cognitive flexibility"],
  25: ["Tactile", "Texture"],                26: ["Proprioception"],
  27: ["Vestibular", "Balance"],             28: ["Auditory", "Discrimination"],
  29: ["Visual", "Tracking"],                30: ["Olfactory", "Gustatory"],
  31: ["Interoception"],                     32: ["Multi-sensory"],
  33: ["Social", "Turn-taking"],             34: ["Communication", "Verbal"],
  35: ["Imitation", "Gesture"],              36: ["Joint attention"],
  37: ["Pragmatic language"],                38: ["Emotional regulation"],
  39: ["Peer interaction"],                  40: ["Non-verbal communication"],
};

const LOREM = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc vulputate libero et velit interdum, ac aliquet odio mattis. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos.";

const PatientActivityPlan = () => {
  const location    = useLocation();
  const { t }       = useTranslation();
  const token       = localStorage.getItem("token");

  const [activeTypeId, setActiveTypeId] = useState(location.state?.typeId || 1);
  const [activityTypes, setActivityTypes] = useState([]);
  const [prescribed, setPrescribed]     = useState([]);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [typesRes, planRes] = await Promise.all([
          fetch(`${API_URL}/activities/types`, { headers }),
          fetch(`${API_URL}/me/activity-plan`,  { headers }),
        ]);
        const typesData = await typesRes.json();
        const planData  = await planRes.json();
        setActivityTypes(Array.isArray(typesData) ? typesData : []);
        setPrescribed(Array.isArray(planData) ? planData : []);
      } catch (err) {
        console.error("PatientActivityPlan fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []); // eslint-disable-line

  const activeType       = activityTypes.find((t) => t.id === activeTypeId) || {};
  const typeActivities   = prescribed.filter((a) => a.activity_type_id === activeTypeId);
  const totalPrescribed  = prescribed.filter((a) => a.activity_type_id === activeTypeId).length;
  const pct              = totalPrescribed > 0 ? 100 : 0;

  return (
    <Layout>
      <TopBar>
        <TopBarLogo>
          <img src={VippstarLogo} alt="Vippstar" style={{ width: 80 }} />
        </TopBarLogo>
        <TopBarContent>
          <SectionTitle>
            {t(`activityTypes.${activeTypeId}.name`, { defaultValue: activeType.name || "" })} Activities
          </SectionTitle>
          <ProgressBarWrapper>
            <ProgressBarTrack>
              <ProgressBarFill $pct={pct} />
            </ProgressBarTrack>
            <ProgressLabel>{totalPrescribed} Prescribed</ProgressLabel>
          </ProgressBarWrapper>
        </TopBarContent>
      </TopBar>

      <PatientSidebar activeNav="activity-plan" />

      <Main>
        <TabsRow>
          {activityTypes.map((type) => (
            <Tab
              key={type.id}
              $active={type.id === activeTypeId}
              onClick={() => setActiveTypeId(type.id)}
            >
              {t(`activityTypes.${type.id}.name`, { defaultValue: type.name })}
            </Tab>
          ))}
        </TabsRow>

        {loading ? (
          <p style={{ color: "#999", fontSize: "0.9rem", textAlign: "center", padding: "40px 0" }}>
            Loading activities…
          </p>
        ) : typeActivities.length === 0 ? (
          <p style={{ color: "#999", fontSize: "0.9rem", textAlign: "center", padding: "40px 0" }}>
            No activities prescribed for this category.
          </p>
        ) : (
          <ActivitiesGrid>
            {typeActivities.map((act) => {
              const tags = ACTIVITY_TAGS[act.id] || [];
              return (
                <ActivityCard key={act.id}>
                  <CardHeader>
                    <CardIcon><ActivityIcon /></CardIcon>
                    <CardName>{act.name}</CardName>
                  </CardHeader>
                  <TagsRow>
                    {tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
                  </TagsRow>
                  <CardDescription>{act.description || LOREM}</CardDescription>
                  <CardButton>Activity card</CardButton>
                </ActivityCard>
              );
            })}
          </ActivitiesGrid>
        )}
      </Main>
    </Layout>
  );
};

export default PatientActivityPlan;
